import { Link } from "react-router-dom";
import { InputAdornment } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { MuiOtpInput } from "mui-one-time-password-input";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { AuthFlow } from "./useAuthFlow";

const fieldLabelSx = { mb: "0.5rem" };

// The shared auth form (email/name/password/OTP + submit + resend/forgot link),
// extracted verbatim from the sign-in page and driven by the useAuthFlow object.
function AuthForm({ flow }: { flow: AuthFlow }): JSX.Element {
  const {
    email,
    fullName,
    password,
    otp,
    emailError,
    isSignup,
    showPassword,
    showOtp,
    forgotPsw,
    emailLocked,
    setEmail,
    setFullName,
    setPassword,
    setOtp,
    setEmailError,
    handleSubmit,
    handleForgotPassword,
    onKeyDownHandler,
    submitLabel,
  } = flow;

  return (
    <MDBox component="form" role="form" onKeyDown={onKeyDownHandler}>
      {!(showOtp || forgotPsw) && (
        <>
          <MDTypography
            sx={fieldLabelSx}
            fontSize="16px"
            fontWeight="medium"
            color="textSecondary"
          >
            Work Email
          </MDTypography>
          <MDBox mb={2}>
            <MDInput
              type="email"
              fullWidth
              disabled={emailLocked || isSignup || showPassword}
              error={emailError !== ""}
              value={email}
              InputLabelProps={{ shrink: true }}
              onChange={(e: any) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
          </MDBox>
          {isSignup && (
            <MDBox mb={2}>
              <MDTypography
                sx={fieldLabelSx}
                fontSize="16px"
                fontWeight="medium"
                color="textSecondary"
              >
                Name
              </MDTypography>
              <MDInput
                InputLabelProps={{ shrink: true }}
                value={fullName}
                onChange={(e: any) => setFullName(e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
          )}
          {(isSignup || showPassword) && (
            <MDBox mb={2}>
              <MDTypography
                sx={fieldLabelSx}
                fontSize="16px"
                fontWeight="medium"
                color="textSecondary"
              >
                Password
              </MDTypography>
              <MDInput
                InputLabelProps={{ shrink: true }}
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                type="password"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
          )}
        </>
      )}
      {!showOtp && forgotPsw && (
        <MDBox mb={2}>
          <MDTypography sx={fieldLabelSx} fontSize="16px" fontWeight="medium" color="textSecondary">
            Password
          </MDTypography>
          <MDInput
            InputLabelProps={{ shrink: true }}
            value={password}
            onChange={(e: any) => setPassword(e.target.value)}
            type="password"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon />
                </InputAdornment>
              ),
            }}
          />
        </MDBox>
      )}
      {showOtp && (
        <MDBox mb={2}>
          <MDTypography sx={fieldLabelSx} fontSize="16px" fontWeight="medium" color="textSecondary">
            Enter OTP
          </MDTypography>
          <MDBox display="flex" justifyContent="center">
            <MuiOtpInput
              value={otp}
              onChange={(val) => setOtp(val)}
              length={4}
              autoFocus
              validateChar={() => true}
              sx={{
                "& .MuiOtpInput-TextField input": {
                  width: "36px",
                  height: "36px",
                  bgcolor: "#fff",
                  padding: "4px",
                  borderRadius: "8px",
                },
              }}
            />
          </MDBox>
        </MDBox>
      )}

      <MDBox mt={4}>
        <MDButton onClick={handleSubmit} color="secondary" size="large" fullWidth>
          {submitLabel}
        </MDButton>
      </MDBox>
      {(showPassword || showOtp) && (
        <MDBox mt={1} textAlign="left">
          <MDTypography variant="button" color="secondary">
            <MDTypography
              component={Link}
              to="#"
              onClick={() => {
                if (showPassword || showOtp) handleForgotPassword(true);
              }}
              variant="button"
              color="primary"
              fontWeight="medium"
              textGradient
              sx={{ ml: 1 }}
            >
              {showOtp ? "Resend OTP?" : "Forgot Password?"}
            </MDTypography>
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );
}

export default AuthForm;
